import React from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { faChevronRight } from "@fortawesome/free-solid-svg-icons"

import Card from "../../../layout/card"
import Icon from "../../../layout/icon"
import GeoBreakDownList from "../geo_breakdown_list"
import GeoBreakdownHeadingLink from "../geo_breakdown_heading_link"

import { brandedReveal, chevron, heading } from "./styles.module.scss"
import {
  COUNTRY_SCALE,
  DISTRICT_ENGLISH_UNITARY,
  DISTRICT_SCOTTISH_UNITARY,
} from "../../../../utils/geographical_scales"
import { InlineText } from "../../../Typography/Typography"
import { renderIf } from "../../../FeatureToggle/FeatureToggle"
import Reveal from "../../../Reveal/Reveal"
import Separator from "../../../Separator/Separator"

const locationsToList = (locations, careServiceType, showSeparator, noMargin) =>
  locations.length > 0 ? (
    <>
      <GeoBreakDownList
        data={locations}
        geographicScale={locations[0].geographicScale}
        districtType={locations[0].districtType}
        noMargin={noMargin}
        careServiceType={careServiceType}
      />
      {showSeparator && <Separator />}
    </>
  ) : null

const linkFromGeosUp = (data, careServiceType) => {
  if (data.length === 0) {
    return null
  }
  const { geographicScale, locationName, districtType } = data[0]
  if (geographicScale === COUNTRY_SCALE) {
    return null
  }
  return (
    <GeoBreakdownHeadingLink
      geographicScale={geographicScale}
      locationName={locationName}
      districtType={districtType}
      geoLevel="geosUp"
      careServiceType={careServiceType}
      copyHandler={(name) => (
        <InlineText brand>
          See all in {name}{" "}
          <Icon icon={faChevronRight} className={classNames(chevron)} />
        </InlineText>
      )}
    />
  )
}

const pageTypes = {
  care_home: "care homes",
  home_care: "home care agencies",
  dementia: "dementia care homes",
}

const getPageTypeText = (pageType) => pageTypes[pageType] || "care providers"

const getParentName = (geosUp) =>
  geosUp.length === 0 ? "your area" : geosUp[0].locationName

const GeoConnectiveLinks = ({
  geosDown,
  geosUp,
  geosSideways,
  careType,
  districtType,
  careServiceType,
}) => {
  const pageType = careServiceType || careType

  const RenderIfUnitaryAuthorityPage = renderIf(
    districtType === DISTRICT_SCOTTISH_UNITARY ||
      districtType === DISTRICT_ENGLISH_UNITARY,
  )

  const RenderIfNotUnitaryAuthorityPage = renderIf(
    districtType !== DISTRICT_SCOTTISH_UNITARY &&
      districtType !== DISTRICT_ENGLISH_UNITARY,
  )

  const titleOnOpen = <InlineText brand>Hide</InlineText>

  return (
    <Card>
      <h2 className={classNames(heading)}>
        Browse {getPageTypeText(pageType)} near you
      </h2>
      {locationsToList(geosDown, careServiceType, true, true)}
      <RenderIfUnitaryAuthorityPage>
        <Reveal
          extraClasses={brandedReveal}
          titleOnOpen={titleOnOpen}
          showChevronNextToTitle
        >
          <InlineText brand>More in {getParentName(geosUp)}</InlineText>
          {locationsToList(geosSideways, careServiceType)}
        </Reveal>
        <Separator />
      </RenderIfUnitaryAuthorityPage>
      <RenderIfNotUnitaryAuthorityPage>
        {locationsToList(geosSideways, careServiceType, true, true)}
      </RenderIfNotUnitaryAuthorityPage>
      {linkFromGeosUp(geosUp, careServiceType)}
    </Card>
  )
}

GeoConnectiveLinks.propTypes = {
  geosDown: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      geographicScale: PropTypes.string,
      districtType: PropTypes.string,
    }),
  ),
  geosUp: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      geographicScale: PropTypes.string,
      districtType: PropTypes.string,
    }),
  ),
  geosSideways: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      geographicScale: PropTypes.string,
      districtType: PropTypes.string,
    }),
  ),
  careType: PropTypes.oneOf(["care_home", "home_care"]),
  districtType: PropTypes.string,
  careServiceType: PropTypes.oneOf(["dementia"]),
}
GeoConnectiveLinks.defaultProps = {
  geosDown: [],
  geosUp: [],
  geosSideways: [],
  careType: undefined,
  districtType: undefined,
  careServiceType: undefined,
}

export default GeoConnectiveLinks
